import { useCallback, useEffect, useRef, useState } from 'react';
import { useService } from '../../../../contexts/ServiceContext';
import { batchProcess } from '../utils/batchProcess';

export interface UseAIOptions {
  maxRetries?: number;
  initialDelay?: number;
  backoffFactor?: number;
  maxDescriptionLength?: number;
  listingKey: string;
  batchSize?: number;
  onComplete?: (results: any) => void; // Callback for when all processing is complete
}

export interface ObjectDetectionResult {
  image_url: string;
  listing_id: string;
  tags: Array<{
    bounding_box: [number, number, number, number];
    centroid: [number, number];
    label: string;
    score: number;
  }>;
}

export interface FlattenedResults {
  [key: string]: Array<{
    fieldKey: string;
    fieldName: string;
    score: number;
    object_label: string;
  }>;
}

export interface UseAIResult {
  generateBatchObjects: (
    mediaItems: Array<{ key: string; url: string }>,
  ) => Promise<void>;
  cancelGeneration: (mediaKey: string) => void;
  isPendingGeneration: () => boolean;
  progress: {
    done: number;
    total: number;
  };
  pendingGenerations: string[];
}

export function useAIObjects(options: UseAIOptions): UseAIResult {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    backoffFactor = 2,
    listingKey: providedListingKey,
    batchSize = 5,
    onComplete,
  } = options;

  const { generateObjectsForImage } = useService();

  const [pendingGenerations, setPendingGenerations] = useState<string[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Track abort controllers for each media item
  const abortControllers = useRef<Record<string, AbortController>>({});

  // Cancel a specific generation
  const cancelGeneration = useCallback((mediaKey: string) => {
    const controller = abortControllers.current[mediaKey];
    if (controller) {
      controller.abort();
      delete abortControllers.current[mediaKey];

      setPendingGenerations((prev) => prev.filter((key) => key !== mediaKey));
    }
  }, []);

  // Clean up abort controllers on unmount
  useEffect(() => {
    return () => {
      Object.values(abortControllers.current).forEach((controller) => {
        if (controller) controller.abort();
      });
    };
  }, []);

  // Generate description with retries
  const generateImageObjects = useCallback(
    async (mediaKey: string, imageUrl: string): Promise<any> => {
      // Use provided listing key or get from context
      const listingKey = providedListingKey;

      if (!listingKey || !mediaKey || !imageUrl) {
        return null;
      }

      // Create abort controller
      const controller = new AbortController();
      abortControllers.current[mediaKey] = controller;

      try {
        // Generate Objects
        const objectsResult = await generateObjectsForImage(
          listingKey,
          imageUrl,
        );

        // Return the description
        return objectsResult;
      } catch (error) {
        console.error('Failed to generate image Objects:', error);
        throw error;
      } finally {
        setPendingGenerations((prev) => prev.filter((key) => key !== mediaKey));

        // Clear abort controller
        delete abortControllers.current[mediaKey];
      }
    },
    [providedListingKey, generateObjectsForImage],
  );

  const generateBatchObjects = useCallback(
    async (mediaItems: Array<{ key: string; url: string }>) => {
      setPendingGenerations(mediaItems.map((item) => item.key));
      setProgress({ done: 0, total: mediaItems.length });
      const results: ObjectDetectionResult[] = await batchProcess(
        mediaItems,
        ({ key, url }) => generateImageObjects(key, url), // processor
        {
          batchSize, // use hook option
          maxRetries, // inherit from hook
          initialRetryDelay: initialDelay,
          backoffFactor,
          onProgress: (done, total) => {
            setProgress({ done, total });
          },
        },
      );

      onComplete?.(
        results.reduce((acc, result: ObjectDetectionResult) => {
          // Get all unique labels from the current result object
          if (result !== null && result !== undefined) {
            Object.entries(result).forEach(([label, details]) => {
              if (!acc[label]) {
                acc[label] = [];
              }

              // Add each detail to the accumulated array for this label if not already exists
              details.forEach((detail: any) => {
                const exists = acc[label].some(
                  (existing) =>
                    existing.fieldKey === detail.fieldKey &&
                    existing.fieldName === detail.fieldName,
                );

                if (!exists) {
                  acc[label].push({
                    score: detail.score,
                    fieldKey: detail.fieldKey,
                    fieldName: detail.fieldName,
                    object_label: detail.object_label,
                  });
                }
              });
            });
          }

          return acc;
        }, {} as FlattenedResults),
      ); // everything processed
    },
    [
      batchSize,
      maxRetries,
      initialDelay,
      backoffFactor,
      onComplete,
      generateImageObjects,
    ],
  );

  const isPendingGeneration = useCallback((): boolean => {
    return pendingGenerations.length > 0;
  }, [pendingGenerations]);

  return {
    generateBatchObjects,
    cancelGeneration,
    isPendingGeneration,
    pendingGenerations,
    progress,
  };
}
